import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { ImagePlus, Trash2 } from 'lucide-react';
import {
  compressBoardGalleryFiles,
  getBoardGalleryImageCaption,
  getBoardGalleryImageCaptionValue,
  MAX_BOARD_GALLERY_IMAGE_DIMENSION,
  MAX_BOARD_GALLERY_IMAGE_MB,
  normalizeBoardGalleryImages,
  resolveBoardGalleryImageSrc
} from '../utils/boardGallery';

const GalleryEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GalleryToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
`;

const GalleryMeta = styled.div`
  font-size: 14px;
  color: #64748b;
`;

const GalleryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: white;
  color: #1e293b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #3b82f6;
    color: #2563eb;
    background: #eff6ff;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const HelperText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: #64748b;
`;

const ErrorText = styled.div`
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 13px;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  padding: 24px;
  border-radius: 12px;
  border: 1px dashed #cbd5e1;
  background: #f8fafc;
  color: #64748b;
  text-align: center;
  font-size: 14px;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  align-items: start;

  & > * {
    width: 100%;
    max-width: 400px;
    justify-self: center;
  }
`;

const PreviewCard = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const PreviewImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  display: block;
`;

const PreviewFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding: 12px;
  background: white;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const PreviewLabel = styled.div`
  font-size: 13px;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CaptionInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13px;
  color: #1e293b;
  background: white;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fff1f2;
  color: #dc2626;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #ffe4e6;
    border-color: #fda4af;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const BoardGalleryEditor = ({ value = [], onChange, disabled = false }) => {
  const inputRef = useRef(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const images = normalizeBoardGalleryImages(value);

  const handleBrowse = () => {
    if (disabled || processing) {
      return;
    }

    inputRef.current?.click();
  };

  const handleFileSelection = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';

    if (!selectedFiles.length) {
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const compressedImages = await compressBoardGalleryFiles(selectedFiles);
      onChange?.([...images, ...compressedImages]);
    } catch (selectionError) {
      setError(selectionError.message || 'Failed to process board gallery images.');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    onChange?.(images.filter((_, index) => index !== indexToRemove));
  };

  const handleCaptionChange = (indexToUpdate, nextCaption) => {
    onChange?.(
      images.map((image, index) => (
        index === indexToUpdate
          ? { ...image, caption: nextCaption }
          : image
      ))
    );
  };

  return (
    <GalleryEditor>
      <GalleryToolbar>
        <GalleryMeta>
          {images.length} image{images.length === 1 ? '' : 's'} selected
        </GalleryMeta>
        <GalleryButton type="button" onClick={handleBrowse} disabled={disabled || processing}>
          <ImagePlus size={16} />
          {processing ? 'Compressing...' : 'Add Images'}
        </GalleryButton>
        <HiddenInput
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleFileSelection}
          disabled={disabled || processing}
        />
      </GalleryToolbar>

      <HelperText>
        Upload one or more board images. Each image is resized so its longest side is {MAX_BOARD_GALLERY_IMAGE_DIMENSION}px and compressed below {MAX_BOARD_GALLERY_IMAGE_MB} MB.
      </HelperText>

      {error && <ErrorText>{error}</ErrorText>}

      {images.length ? (
        <PreviewGrid>
          {images.map((image, index) => (
            <PreviewCard key={`gallery-image-${index}`}>
              <PreviewImage
                src={resolveBoardGalleryImageSrc(image)}
                alt={getBoardGalleryImageCaption(image) || `Board gallery ${index + 1}`}
                loading="lazy"
              />
              <PreviewFooter>
                <PreviewHeader>
                  <PreviewLabel>Image {index + 1}</PreviewLabel>
                  <RemoveButton
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    disabled={disabled || processing}
                    aria-label={`Remove gallery image ${index + 1}`}
                  >
                    <Trash2 size={14} />
                  </RemoveButton>
                </PreviewHeader>
                <CaptionInput
                  type="text"
                  value={getBoardGalleryImageCaptionValue(image)}
                  onChange={(event) => handleCaptionChange(index, event.target.value)}
                  placeholder="Add a caption (optional)"
                  disabled={disabled || processing}
                  aria-label={`Caption for gallery image ${index + 1}`}
                />
              </PreviewFooter>
            </PreviewCard>
          ))}
        </PreviewGrid>
      ) : (
        <EmptyState>No gallery images uploaded yet.</EmptyState>
      )}
    </GalleryEditor>
  );
};

export default BoardGalleryEditor;
